import { MaterialCommunityIcons, MaterialIcons } from '@expo/vector-icons';
import {
  calculateDateFromInterval,
  calculateHoursDifference,
  calculateMinutesDifference,
  DayIntervalUnit,
  formatDateToMMDDYYYY,
  MS_TO_UNITS,
  RoutineWorkflow,
  useAssetRoutines,
  useUpdateMutation,
  useWorkspaceRoutine,
  WorkspaceAsset
} from '@gripp/shared-logic';
import * as Haptics from 'expo-haptics';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { Colors } from '../../../themes/colors';
import { AlertTriggerConfigKeys } from './types';

export const excludedConfigKeys = [
  'workspaceRoutineId',
  'routineType',
  'startDate',
  'onIntervalStart',
  'triggerCounterRoutineId',
  'resetDate',
  'dayInterval'
];

export type AlertSummaryProps = {
  workspaceAsset: WorkspaceAsset;
  routineId: string;
  workflow: RoutineWorkflow;
  onAlertSelected: (alert: RoutineWorkflow) => void;
};

export const AlertSummary = ({
  workspaceAsset,
  routineId,
  workflow,
  onAlertSelected,
}: AlertSummaryProps) => {
  const isIos = Platform.OS === 'ios';
  const { trigger, uom, count } = workflow;
  const { t } = useTranslation();
  const triggerCounterId = trigger.config.triggerCounterRoutineId || '';

  const [hideSummary, setHideSummary] = useState(false);
  const { mutateAsync: updateWorkflow, isPending } = useUpdateMutation({
    query: UPDATE_WORKFLOW,
    modelName: 'workflow',
  });

  const triggerWorkspaceRoutine = useWorkspaceRoutine(triggerCounterId);
  const triggerUOM = triggerWorkspaceRoutine?.config?.uom;
  const triggerConfigKeys = Object.keys(trigger.config);

  // Get completion count using custom resolver
  const { assetRoutinesCount } = useAssetRoutines(
    workspaceAsset?.id,
    triggerCounterId || workflow.trigger.config.workspaceRoutineId,
    (trigger.config as any)?.resetDate
  );

  const filteredKeys = triggerConfigKeys.filter((name) => {
    return !excludedConfigKeys.includes(name);
  });

  const routineState = workspaceAsset?.routineState;

  const counterOrRoutineId =
    triggerCounterId || workflow.trigger.config.workspaceRoutineId;

  const latestRoutineState = useMemo(() => {
    if (!routineState || !counterOrRoutineId) return undefined;

    const result = routineState.find(
      (state) => state.workspaceRoutineId === counterOrRoutineId
    );

    return result;
  }, [routineState, counterOrRoutineId]);

  const lastRoutineDate = useMemo(() => {
    return latestRoutineState?.state.updatedAt;
  }, [latestRoutineState]);

  // Extract lastTriggered date from workflow state
  const lastAlertDate = useMemo(() => {
    if (!workspaceAsset?.workflowState || !workflow.id) return undefined;
    
    const workflowState = workspaceAsset.workflowState.find(
      (state) => state.workflowId === workflow.id
    );
    
    return (workflowState?.triggerState as any)?.lastTriggered;
  }, [workspaceAsset?.workflowState, workflow.id]);

  const getSkippedCompletionDateOrDays = (
    date: Date,
    daysVal: number,
    getDate: boolean
  ) => {
    if (!date || !daysVal) return 'error';

    const nextDate = new Date(date);
    nextDate.setDate(nextDate.getDate() + Number(daysVal));

    const now = new Date();
    const lastUpdated = new Date(date);
    let daysElapsed = Math.abs(
      Math.floor((now.getTime() - lastUpdated.getTime()) / MS_TO_UNITS[DayIntervalUnit.Days])
    );

    while (nextDate < now || daysElapsed > daysVal) {
      nextDate.setDate(nextDate.getDate() + Number(daysVal));
      daysElapsed = daysElapsed - daysVal;
    }

    return getDate ? formatDateToMMDDYYYY(nextDate) : daysElapsed;
  };

  const getDifferenceInDays = (dateStr: string) => {
    const alertDate = new Date(dateStr);
    const now = new Date();

  const differenceTime = alertDate.getTime() - now.getTime();
    const differenceDays =
      differenceTime < 0 ? 0 : Math.ceil(differenceTime / MS_TO_UNITS[DayIntervalUnit.Days]);

    return differenceDays;
  };

  const getNextEveryDateOrDays = (
    dateStr: string,
    daysValue: number,
    returnDaysValue: boolean
  ) => {
    if (!dateStr || !daysValue) return 'invalid date..';
    const interval = Number(daysValue);
    const now = new Date();
    const start = new Date(dateStr);
    const next = new Date(start);
    while (next <= now) {
      next.setDate(next.getDate() + interval);
    }
    if (returnDaysValue) {
      const daysUntil = Math.ceil((next.getTime() - now.getTime()) / MS_TO_UNITS[DayIntervalUnit.Days]);
      return daysUntil < 0 ? 0 : daysUntil;
    }
    return formatDateToMMDDYYYY(next);
  };

  const getTriggerLabel = (key: AlertTriggerConfigKeys) => {
    let alertValue = trigger.config[key];
    let nextAlert = t('alerts.nextAlert');
    let currentState = t('alerts.currently');
    let daysValue;
    let daysLabel: string;
    let difference: number;
    const intervalStartValue = trigger.config.onIntervalStart;
    let nextInterval;

    const AlertLabels = () => (
      <>
        <Text style={styles.subHeaderLabel}>{nextAlert}</Text>
        <Text style={styles.subHeaderLabel}>{currentState}</Text>
        <Text style={styles.subHeaderLabel}>
          {t('alerts.lastAlert')}
          {lastAlertDate ? formatDateToMMDDYYYY(new Date(lastAlertDate)) : t('alerts.lastAlertNA')}
        </Text>
      </>
    );

    switch (key) {
      case AlertTriggerConfigKeys.dayIntervalUnit: {
        if (
          !trigger.config.dayInterval || 
          !trigger.config.dayIntervalUnit ||
          !trigger.config.startDate
        ) {
          return;
        }

        const unit = trigger.config.dayIntervalUnit;
        const intervalValue = trigger.config.dayInterval;
        const baselineStart = (trigger.config as any)?.resetDate || trigger.config.startDate;
        const targetDate = calculateDateFromInterval(
          intervalValue,
          unit,
          baselineStart
        );
        const now = new Date();

        switch (unit) {
          case DayIntervalUnit.Minutes: {
            daysValue = calculateMinutesDifference(targetDate, now);
            daysLabel = daysValue > 1 ? t('alerts.minutes') : t('alerts.minute');
            break;
          }
          case DayIntervalUnit.Hours: {
            daysValue = calculateHoursDifference(targetDate, now);
            daysLabel = daysValue > 1 ? t('alerts.hours') : t('alerts.hour');
            break;
          }
          default: {
            daysValue = getDifferenceInDays(targetDate.toISOString());
            daysLabel = daysValue > 1 ? t('alerts.days') : t('alerts.day');
          }
        }

        // Calculate the newDate for display
        nextAlert += formatDateToMMDDYYYY(targetDate.toISOString());

        currentState += `${daysValue} ${daysLabel} ${t('alerts.togo')}`;

        return <AlertLabels />;
      }
      case AlertTriggerConfigKeys.onDate:
        if (typeof alertValue === 'string') {
          daysValue = getDifferenceInDays(alertValue);
        } else if (alertValue instanceof Date) {
          daysValue = getDifferenceInDays(alertValue.toISOString());
        } else {
          daysValue = 0;
        }

        // Hide all onDate alerts that are past due
        if (daysValue === 0) {
          setHideSummary(true);
          return;
        }
        
        daysLabel =
          daysValue > 1 || daysValue < 1 ? t('alerts.days') : t('alerts.day');

        nextAlert += formatDateToMMDDYYYY(
          alertValue instanceof Date ? alertValue : (alertValue as string)
        );
        currentState += `${daysValue} ${daysLabel} ${t('alerts.togo')}`;

        return <AlertLabels />;
      case AlertTriggerConfigKeys.onInterval: {
        const latestRoutineStateCount =
          Number(latestRoutineState?.state?.count) || 0;

        if (intervalStartValue) {
          nextInterval = Number(intervalStartValue) + Number(alertValue);

          difference = nextInterval - latestRoutineStateCount;

          if (difference < 0) {
            nextInterval = latestRoutineStateCount + Number(alertValue);
            difference = Number(alertValue);
          }

          nextAlert += `${nextInterval} ${triggerUOM}`;
          currentState += `${difference} ${triggerUOM} ${t('alerts.togo')}`;
        } else {
          difference = Math.abs(Number(alertValue) - latestRoutineStateCount);
          nextAlert += `${alertValue} ${triggerUOM}`;
          currentState += `${difference} ${triggerUOM} ${t('alerts.togo')}`;
        }
        return <AlertLabels />;
      }
      case AlertTriggerConfigKeys.every: {
        const baselineStart = (trigger.config as any)?.resetDate || trigger.config.startDate;
        if (baselineStart) {
          daysValue = getNextEveryDateOrDays(
            baselineStart.toString(),
            Number(alertValue),
            true
          );

          nextAlert += getNextEveryDateOrDays(
            baselineStart.toString(),
            Number(alertValue),
            false
          );
        }
        currentState += daysValue;

        daysLabel =
          Number(daysValue) > 1 || Number(daysValue) < 1
            ? t('alerts.days')
            : t('alerts.day');

        currentState += ` ${daysLabel} ${t('alerts.togo')}`;

        return <AlertLabels />;
      }
      case AlertTriggerConfigKeys.floor:
        nextAlert += `${alertValue} ${uom}`;
        currentState += `${count || '--'} ${uom}`;
        return <AlertLabels />;

      case AlertTriggerConfigKeys.ceiling:
        nextAlert += `${alertValue} ${uom}`;
        currentState += `${count || '--'} ${uom}`;
        return <AlertLabels />;

      case AlertTriggerConfigKeys.onCompletionCount: {
        const routinesCount = assetRoutinesCount?.count || 0;
        const effectiveCount = routinesCount;
        
        difference = Number(difference);
        alertValue = Number(alertValue);

        difference = alertValue - (effectiveCount % alertValue);

        if (difference < 0) {
          difference = 0;
        }

        nextAlert += `${difference} ${t('alerts.completions')} ${t(
          'alerts.togo'
        )}`;
        currentState += `${effectiveCount} ${t('alerts.completions')} ${t(
          'alerts.recorded'
        )}`;
        return <AlertLabels />;
      }
      case AlertTriggerConfigKeys.daysSinceCompleted: {
        const daysSinceValue = Number(alertValue) || 0;

        // Use resetDate if available, otherwise fall back to last completion or creation date
        let referenceDate = (trigger.config as any)?.resetDate
          ? new Date((trigger.config as any).resetDate)
          : undefined;

        if (!referenceDate) {
          referenceDate = lastRoutineDate;
        }

        if (!referenceDate) {
          referenceDate = workflow.createdAt
            ? new Date(workflow.createdAt)
            : new Date();
          if (!workflow.createdAt) {
            referenceDate.setDate(referenceDate.getDate() - daysSinceValue);
          }
        }

        nextAlert += `${getSkippedCompletionDateOrDays(
          referenceDate,
          daysSinceValue,
          true
        )}`;
        currentState += `${getSkippedCompletionDateOrDays(
          referenceDate,
          daysSinceValue,
          false
        )} ${t('alerts.days')} ${t('alerts.elapsed')}`;
        return <AlertLabels />;
      }
      default:
        return;
    }
  };

  const includeResetButton = useMemo(() => {
    const alertsWithReset = [
      AlertTriggerConfigKeys.onInterval,
      AlertTriggerConfigKeys.every,
      AlertTriggerConfigKeys.onCompletionCount,
      AlertTriggerConfigKeys.daysSinceCompleted,
    ];

    return filteredKeys.some((item) =>
      alertsWithReset.includes(item as AlertTriggerConfigKeys)
    );
  }, [workflow]);

  const onResetPress = async () => {
    const resetDate = new Date().toISOString();
    const currentCount = latestRoutineState?.state.count || 0;

    const resetConfig: any = {
      ...trigger.config,
      resetDate,
    };

    if (filteredKeys.includes(AlertTriggerConfigKeys.onInterval)) {
      resetConfig.onIntervalStart = String(currentCount);
    }

    const input = {
      id: workflow.id,
      trigger: {
        ...trigger,
        config: resetConfig,
      },
    };

    await updateWorkflow({ input: input });
  };

  const ResetButton = () => {
    return (
      <Pressable
        style={[styles.resetButton, isPending && styles.resetButtonActive]}
        onPress={() => {
          isIos && Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
          onResetPress();
        }}
      >
        <MaterialCommunityIcons
          name={'cached'}
          size={18}
          style={{ color: isPending ? Colors.blue : Colors.white }}
        />
        <Text
          style={[
            styles.resetLabel,
            { color: isPending ? Colors.blue : Colors.white },
          ]}
        >
          {t('alerts.reset')}
        </Text>
      </Pressable>
    );
  };

  const AlertTriggerLabels = () => {
    return filteredKeys.map((key, index) => {
      return (
        <View key={key}>
          {getTriggerLabel(key as AlertTriggerConfigKeys)}
          {index < filteredKeys.length - 1 && (
            <Text style={styles.subHeaderLabel}>{t('alerts.OR')}</Text>
          )}
        </View>
      );
    });
  };

  const onAlertPress = (workflow: RoutineWorkflow) => {
    onAlertSelected(workflow);
  };

  return (
    !hideSummary && (
      <Pressable onPress={() => onAlertPress(workflow)} style={styles.container}>
        <View style={styles.leftSection}>
          <Text
            allowFontScaling={false}
            adjustsFontSizeToFit={true}
            style={styles.headerLabel}
          >
            {workflow.name || t('alerts.alert')}
          </Text>
          <View style={styles.subHeaderContainer}>
            <AlertTriggerLabels />
          </View>
        </View>
        <View style={styles.rightSection}>
          <View style={styles.buttonContainer}>
            {includeResetButton && <ResetButton />}
            <MaterialIcons name="arrow-forward-ios" style={[styles.icon]} />
          </View>
        </View>
      </Pressable>
    )
  );
};

const styles = StyleSheet.create({
  container: {
    minHeight: 92,
    backgroundColor: Colors.white,
    borderRadius: 8,
    marginBottom: 12,
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    paddingVertical: 20,
    paddingHorizontal: 16,
    columnGap: 8,
  },
  leftSection: {
    flex: 10 / 15,
    height: '100%',
    overflow: 'hidden',
  },
  rightSection: {
    flex: 5 / 15,
    height: '100%',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  headerLabel: {
    fontSize: 16,
    fontWeight: '600',
    letterSpacing: -0.25,
    color: Colors.blackText,
  },
  subHeaderContainer: {
    flex: 1,
    justifyContent: 'flex-start',
    marginTop: 8,
    flexWrap: 'nowrap',
  },
  subHeaderLabel: {
    fontSize: 12,
    fontWeight: '500',
    letterSpacing: -0.2,
    color: Colors.grayFill,
  },
  buttonContainer: {
    columnGap: 12,
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    flex: 1,
  },
  resetButton: {
    flexDirection: 'row',
    justifyContent: 'center',
    minWidth: 70,
    alignItems: 'center',
    borderWidth: 2,
    backgroundColor: Colors.blue,
    borderColor: Colors.blue,

    borderRadius: 8,
    padding: 6,
    columnGap: 4,
  },
  resetButtonActive: {
    borderColor: Colors.blue,
    backgroundColor: Colors.white,
  },
  resetLabel: {
    fontSize: 14,
    fontWeight: '500',
    letterSpacing: -0.25,
  },
  icon: {
    fontSize: 24,
  },
});

const UPDATE_WORKFLOW = `
  mutation UpdateWorkflow($input: MutateWorkflowInput!) {
  updateWorkflow(input: $input) {
    id
  }
}
`;
